import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import {
  Briefcase,
  MapPin,
  DollarSign,
  Calendar,
  Clock,
  XCircle,
  CheckCircle
} from 'lucide-react';
import { EmptyState } from '../shared/EmptyState';

interface Application {
  id: string;
  status: string;
  message: string;
  proposed_rate: number;
  created_at: string;
  updated_at: string;
  withdrawn_at: string | null;
  notes: string | null;
  job: {
    id: string;
    title: string;
    location: string;
    pay_rate: number;
    pay_type: string;
    trade_type: string;
    start_date: string;
    status: string;
  } | null;
}

export const ApplicationsView: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'new' | 'contacted' | 'verified' | 'hired' | 'rejected' | 'withdrawn'>('all');
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState<string | null>(null);

  useEffect(() => {
    if (profile) {
      loadApplications();
    }
  }, [profile]);

  useEffect(() => {
    if (!profile) return;

    const channel = supabase
      .channel('applications-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'applications',
          filter: `contractor_id=eq.${profile.id}`,
        },
        () => {
          loadApplications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile]);

  const loadApplications = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('applications')
        .select(`
          *,
          job:jobs (
            id,
            title,
            location,
            pay_rate,
            pay_type,
            trade_type,
            start_date,
            status
          )
        `)
        .eq('contractor_id', profile.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      showToast('Failed to load applications', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (applicationId: string) => {
    setWithdrawingId(applicationId);
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: 'withdrawn' })
        .eq('id', applicationId);

      if (error) throw error;

      showToast('Application withdrawn successfully', 'success');
      setConfirmWithdraw(null);
      await loadApplications();
    } catch (error) {
      console.error('Error withdrawing application:', error);
      showToast('Failed to withdraw application', 'error');
    } finally {
      setWithdrawingId(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300';
      case 'contacted':
        return 'bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border border-amber-300';
      case 'verified':
        return 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300';
      case 'hired':
        return 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800 border border-emerald-300';
      case 'rejected':
        return 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300';
      case 'withdrawn':
        return 'bg-gradient-to-r from-slate-100 to-slate-200 text-slate-800 border border-slate-300';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'hired':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
      case 'withdrawn':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const filteredApplications = applications.filter((app) =>
    filter === 'all' ? true : app.status === filter
  );

  const statusCounts = {
    all: applications.length,
    new: applications.filter((a) => a.status === 'new').length,
    contacted: applications.filter((a) => a.status === 'contacted').length,
    verified: applications.filter((a) => a.status === 'verified').length,
    hired: applications.filter((a) => a.status === 'hired').length,
    rejected: applications.filter((a) => a.status === 'rejected').length,
    withdrawn: applications.filter((a) => a.status === 'withdrawn').length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-slate-900">My Applications</h1>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 rounded-2xl p-8 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">My Applications</h1>
              <p className="text-slate-300">
                {filteredApplications.length} {filteredApplications.length === 1 ? 'application' : 'applications'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.entries(statusCounts).map(([status, count]) => (
          <button
            key={status}
            onClick={() => setFilter(status as any)}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              filter === status
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-blue-300'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
          </button>
        ))}
      </div>

      {filteredApplications.length === 0 ? (
        <EmptyState
          icon={Briefcase}
          title="No applications found"
          description={
            filter === 'all'
              ? 'Start applying to jobs to see your applications here'
              : `No ${filter} applications at the moment`
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredApplications.map((application) => {
            if (!application.job) return null;

            return (
            <div
              key={application.id}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-xl hover:border-blue-300 transition-all hover:scale-[1.01]"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-slate-900">
                      {application.job.title}
                    </h3>
                    <span
                      className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        application.status
                      )}`}
                    >
                      {getStatusIcon(application.status)}
                      <span className="capitalize">{application.status}</span>
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                    <div className="flex items-center space-x-1">
                      <Briefcase className="w-4 h-4" />
                      <span>{application.job.trade_type}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <MapPin className="w-4 h-4" />
                      <span>{application.job.location}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <DollarSign className="w-4 h-4" />
                      <span>
                        ${application.job.pay_rate}/{application.job.pay_type}
                      </span>
                    </div>
                    {application.job.start_date && (
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>Starts {formatDate(application.job.start_date)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {application.message && (
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-700">{application.message}</p>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="text-xs text-slate-500">
                  Applied {formatDate(application.created_at)}
                  {application.withdrawn_at && (
                    <span className="ml-2">
                      • Withdrawn {formatDate(application.withdrawn_at)}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {application.status !== 'withdrawn' &&
                    application.status !== 'hired' &&
                    application.status !== 'rejected' &&
                    application.job.status === 'open' && (
                      <button
                        onClick={() => setConfirmWithdraw(application.id)}
                        disabled={withdrawingId === application.id}
                        className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {withdrawingId === application.id ? 'Withdrawing...' : 'Withdraw'}
                      </button>
                    )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmWithdraw}
        onClose={() => setConfirmWithdraw(null)}
        onConfirm={() => confirmWithdraw && handleWithdraw(confirmWithdraw)}
        title="Withdraw Application?"
        message="Are you sure you want to withdraw this application? This action cannot be undone."
        confirmText="Withdraw"
        cancelText="Cancel"
        variant="danger"
        loading={!!withdrawingId}
      />
    </div>
  );
};
